import React, { useState, useEffect, useRef } from "react";
import "../../pages/formulaire/formulaire.css";
import { useNavigate } from "react-router-dom";
const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL_PROD // https://demenagementsamyyhaul.ca/api
    : process.env.REACT_APP_API_URL_DEV; // http://158.69.205.231:3000

const Demenagement = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);
  const [itemsState, setItemsState] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extraServices, setExtraServices] = useState([]);
  const [isDateUnavailable, setIsDateUnavailable] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]); // ← NOUVEAU
  const [imageUploadError, setImageUploadError] = useState(""); // ← NOUVEAU
  const navigate = useNavigate();

  // Check if user is authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [guestToken, setGuestToken] = useState(null);
  const [modeSelection, setModeSelection] = useState(null); // "articles" ou "manuelle"

  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    tel: "",
    adresse_depart: "",
    ville_depart: "",
    cp_depart: "",
    adresse_arrivee: "",
    ville_arrivee: "",
    cp_arrivee: "",
    services: [], // Ensure services is always an array
    objets_speciaux: "",
    notes: "",
    rgpd: false,
    date_demenagement: "",
    plage_horaire: "",
    type_hebergement_depart: "",
    etage_depart: "",
    ascenseur_depart: "",
    type_hebergement_arrivee: "",
    etage_arrivee: "",
    ascenseur_arrivee: "",
    date_debut_custom: "",
    date_fin_custom: "",
    departureAddressId: null,
    arrivalAddressId: null,
    nombre_camions: "",        // ← NOUVEAU
    nombre_demenageurs: "",    // ← NOUVEAU
    description_manuelle: "",  // ← NOUVEAU (pour la page 2)
  });

  const fieldRefs = useRef({
    prenom: null,
    nom: null,
    email: null,
    tel: null,
    adresse_depart: null,
    ville_depart: null,
    cp_depart: null,
    adresse_arrivee: null,
    ville_arrivee: null,
    cp_arrivee: null,
    images: null, // ← NOUVEAU
  });

  // Check authentication status on component mount
  useEffect(() => {
    console.log("Checking authentication status...");

    const token = localStorage.getItem("authToken");
    const currentUserStr = localStorage.getItem("currentUser");

    if (token && currentUserStr) {
      try {
        const currentUser = JSON.parse(currentUserStr);
        setIsAuthenticated(true);

        if (currentUser.id) {
          setUserId(currentUser.id.toString());
          console.log("User is authenticated with ID:", currentUser.id);

          // Nettoyer le guestToken si l'utilisateur est connecté
          localStorage.removeItem("guestToken");
          setGuestToken(null);
        } else {
          const userIdFromStorage = localStorage.getItem("userId");
          if (userIdFromStorage) {
            setUserId(userIdFromStorage);
            console.log("User ID from separate storage:", userIdFromStorage);
          } else {
            console.warn("User is authenticated but no ID found in storage");
          }
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
      console.log("User is not authenticated - will collect guest info");

      // Initialiser le guestToken pour les non-authentifiés
      const guestToken = manageGuestToken();
      setGuestToken(guestToken);
    }
  }, []);

  // Pre-fill user information when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const currentUserStr = localStorage.getItem("currentUser");
      if (currentUserStr) {
        try {
          const currentUser = JSON.parse(currentUserStr);
          setFormData((prev) => ({
            ...prev,
            prenom: currentUser.firstName || currentUser.prenom || "",
            nom: currentUser.lastName || currentUser.nom || "",
            email: currentUser.email || "",
            tel: currentUser.phone || currentUser.tel || "",
          }));
        } catch (error) {
          console.error("Error parsing user data:", error);
        }
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        console.log("Fetching initial data...");
        setLoading(true);

        // Fetch categories
        const categoriesResponse = await fetch(`${API_BASE_URL}/category`);
        const categoriesData = await categoriesResponse.json();
        console.log("Categories fetched:", categoriesData);

        // Fetch category items
        const itemsResponse = await fetch(`${API_BASE_URL}/category-item`);
        const itemsData = await itemsResponse.json();
        console.log("Category items fetched:", itemsData);

        // Fetch extra services
        const servicesResponse = await fetch(`${API_BASE_URL}/extra-service`);
        const servicesData = await servicesResponse.json();
        console.log("Extra services fetched:", servicesData);

        // Set categories and category items
        setCategories(categoriesData.data.categories);
        setCategoryItems(itemsData.data.categoryItems);

        // Handle extra services
        if (
          servicesData.statusCode === 200 &&
          servicesData.data?.extraServices
        ) {
          const validServices = servicesData.data.extraServices.filter(
            (service) =>
              service &&
              service.id &&
              service.name &&
              typeof service.name === "string"
          );
          setExtraServices(validServices);
          console.log("Valid extra services loaded:", validServices.length);
        } else {
          console.warn("No valid extra services found in API response");
          setExtraServices([]);
        }

        // Initialize items state
        const initialState = {};
        itemsData.data.categoryItems.forEach((item) => {
          initialState[item.id] = {
            id: item.id,
            checked: false,
            quantity: 1,
            name: item.name,
            icon: item.icon,
            categoryId: item.category.id,
          };
        });

        setItemsState(initialState);
        console.log("Initial data loaded successfully");
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch unavailable dates on component mount
  useEffect(() => {
    fetchUnavailableDates("demenagement");
  }, []);
  // Fonction pour gérer l'upload d'images
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Validation des fichiers
    const validFiles = files.filter((file) => {
      // Vérifier le type de fichier
      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!validTypes.includes(file.type)) {
        alert(
          `Le fichier ${file.name} n'est pas une image valide. Formats acceptés: JPEG, PNG, GIF, WebP`
        );
        return false;
      }

      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(
          `Le fichier ${file.name} est trop volumineux. Taille maximale: 5MB`
        );
        return false;
      }

      return true;
    });

    // Limiter à 5 images maximum
    const totalImages = uploadedImages.length + validFiles.length;
    if (totalImages > 5) {
      alert("Vous ne pouvez pas uploader plus de 5 images");
      validFiles.splice(5 - uploadedImages.length);
    }

    // Créer des URLs pour l'aperçu
    const newImages = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2) + " MB",
    }));

    setUploadedImages((prev) => [...prev, ...newImages]);
    setImageUploadError(""); // Effacer les erreurs précédentes
  };

  // Fonction pour supprimer une image
  const removeImage = (index) => {
    setUploadedImages((prev) => {
      const newImages = [...prev];
      // Libérer l'URL de l'image supprimée
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Fonction pour uploader les images vers le serveur
  // 1. First, remove the export and fix the uploadImagesToServer function inside your component:

  const uploadImagesToServer = async (uploadedImages) => {
    if (!uploadedImages || uploadedImages.length === 0) {
      throw new Error("Aucune image à uploader");
    }

    console.log("📸 Starting image upload process...");
    console.log("Number of images to upload:", uploadedImages.length);

    // Log each image details for debugging
    uploadedImages.forEach((img, index) => {
      console.log(`Image ${index + 1}:`, {
        name: img.file.name,
        size: img.file.size,
        type: img.file.type,
        lastModified: new Date(img.file.lastModified).toISOString(),
      });
    });

    const formData = new FormData();

    // Validate files before upload
    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    const maxFileSize = 5 * 1024 * 1024; // 5MB

    for (let i = 0; i < uploadedImages.length; i++) {
      const file = uploadedImages[i].file;

      // Validate file type
      if (!validImageTypes.includes(file.type)) {
        throw new Error(
          `Le fichier ${
            file.name
          } n'est pas un type d'image valide. Types acceptés: ${validImageTypes.join(
            ", "
          )}`
        );
      }

      // Validate file size
      if (file.size > maxFileSize) {
        throw new Error(
          `Le fichier ${file.name} est trop volumineux (${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB). Taille maximale: 5MB`
        );
      }
    }

    // If multiple images → backend expects "images"
    if (uploadedImages.length > 1) {
      uploadedImages.forEach((image) => {
        formData.append("images", image.file);
      });
    } else {
      // If single image → backend expects "image"
      formData.append("image", uploadedImages[0].file);
    }

    // Choose endpoint automatically
    const endpoint =
      uploadedImages.length > 1
        ? `${API_BASE_URL}/test-upload/multiple-images`
        : `${API_BASE_URL}/test-upload/single-image`;
    console.log("➡️ Uploading to:", endpoint);
    console.log("📦 FormData contents:");
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`${key}:`, {
          name: value.name,
          size: value.size,
          type: value.type,
        });
      } else {
        console.log(`${key}:`, value);
      }
    }

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
      });

      console.log("📡 Upload response status:", response.status);
      console.log(
        "📡 Upload response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          console.error("❌ Upload error response:", errorData);
          errorMessage = errorData.message || errorData.error || errorMessage;

          // Specific error handling for common issues
          if (response.status === 400) {
            if (errorData.message?.includes("Invalid file format")) {
              errorMessage =
                "Format de fichier invalide. Utilisez JPG, PNG, GIF ou WebP.";
            } else if (errorData.message?.includes("file too large")) {
              errorMessage = "Fichier trop volumineux. Taille maximale: 5MB.";
            } else if (errorData.message?.includes("No file")) {
              errorMessage = "Aucun fichier reçu par le serveur.";
            }
          }
        } catch (parseError) {
          const errorText = await response.text();
          console.error("❌ Upload error (raw):", errorText);
          errorMessage = errorText || errorMessage;
        }

        throw new Error(`Erreur upload: ${errorMessage}`);
      }

      const data = await response.json();
      console.log("✅ Upload success:", data);

      // Extract URLs from the response based on your backend structure
      if (uploadedImages.length > 1) {
        // Multiple images: data.data is an array
        if (!data.data || !Array.isArray(data.data)) {
          console.error(
            "Unexpected response structure for multiple images:",
            data
          );
          throw new Error("Format de réponse inattendu du serveur");
        }
        return data.data.map((item) => item.secure_url);
      } else {
        // Single image: data.data is an object
        if (!data.data || !data.data.secure_url) {
          console.error(
            "Unexpected response structure for single image:",
            data
          );
          throw new Error("Format de réponse inattendu du serveur");
        }
        return [data.data.secure_url];
      }
    } catch (error) {
      console.error("❌ Upload failed:", error);

      // Provide more specific error messages
      if (error.message.includes("Failed to fetch")) {
        throw new Error(
          "Erreur de connexion au serveur. Vérifiez votre connexion internet."
        );
      } else if (error.message.includes("NetworkError")) {
        throw new Error(
          "Erreur réseau. Le serveur est peut-être temporairement indisponible."
        );
      }

      throw error;
    }
  };

  // Fetch unavailable dates for a specific service type
  const fetchUnavailableDates = async (serviceType = "demenagement") => {
    try {
      console.log(`Fetching unavailable dates for ${serviceType}...`);
      const response = await fetch(
        `${API_BASE_URL}/service-unavailable-date?serviceType=${serviceType}`
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Unavailable dates fetched:", data);
        setUnavailableDates(data.data?.unavailableDates || []);
        return data.data?.unavailableDates || [];
      } else {
        console.warn("Failed to fetch unavailable dates");
        return [];
      }
    } catch (error) {
      console.error("Error fetching unavailable dates:", error);
      return [];
    }
  };

  // NEW: Check date availability using the specific API endpoint
  const checkDateAvailability = async (date, serviceType = "demenagement") => {
    try {
      console.log(
        `Checking availability for date: ${date}, serviceType: ${serviceType}`
      );

      const response = await fetch(
        `${API_BASE_URL}/service-unavailable-date/check-availability?date=${date}&serviceType=${serviceType}`
      );
      if (response.ok) {
        const data = await response.json();
        console.log("Date availability check result:", data);

        if (data.statusCode === 200) {
          return {
            isAvailable: data.data.isAvailable,
            message: data.data.message,
          };
        }
      } else {
        console.warn("Failed to check date availability");
        return { isAvailable: true, message: "Could not verify availability" };
      }
    } catch (error) {
      console.error("Error checking date availability:", error);
      return { isAvailable: true, message: "Error checking availability" };
    }

    return { isAvailable: true, message: "Default to available" };
  };

  // Check if a specific date is unavailable (legacy method - keeping for backward compatibility)
  const isDateUnavailableCheck = (dateToCheck) => {
    if (!unavailableDates.length) return false;

    return unavailableDates.some((dateRange) => {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      const checkDate = new Date(dateToCheck);

      // Check if the date falls within any unavailable range
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Check if a date range overlaps with unavailable dates
  const isDateRangeUnavailable = (startDate, endDate) => {
    if (!unavailableDates.length) return false;

    const rangeStart = new Date(startDate);
    const rangeEnd = new Date(endDate);

    return unavailableDates.some((dateRange) => {
      const unavailableStart = new Date(dateRange.startDate);
      const unavailableEnd = new Date(dateRange.endDate);

      // Check for overlap between ranges
      return (
        (rangeStart <= unavailableEnd && rangeEnd >= unavailableStart) ||
        (unavailableStart <= rangeEnd && unavailableEnd >= rangeStart)
      );
    });
  };

  // UPDATED: Handle date selection with new API availability check
  const handleDateSelection = async (selectedDate) => {
    console.log(`Date selected: ${selectedDate}`);

    setFormData((prev) => ({
      ...prev,
      date_demenagement: selectedDate,
      date_debut_custom: "",
      date_fin_custom: "",
    }));

    // Check if selected date is unavailable using the new API endpoint
    const availabilityResult = await checkDateAvailability(
      selectedDate,
      "demenagement"
    );
    console.log("Date availability result:", availabilityResult);

    const unavailable = !availabilityResult.isAvailable;
    setIsDateUnavailable(unavailable);

    if (unavailable) {
      console.log("Date is unavailable, showing custom date range fields");
      // Show message to user
      alert(
        `Cette date n'est pas disponible. ${
          availabilityResult.message ||
          "Veuillez choisir une autre date ou spécifier un intervalle de dates préféré."
        }`
      );

      // Also refresh the unavailable dates list for better UI feedback
      await fetchUnavailableDates("demenagement");
    } else {
      console.log("Date is available, proceeding normally");
    }
  };

  // Handle custom date range selection with validation
  const handleCustomDateRange = (startDate, endDate) => {
    console.log(`Custom date range selected: ${startDate} to ${endDate}`);

    setFormData((prev) => ({
      ...prev,
      date_debut_custom: startDate,
      date_fin_custom: endDate,
    }));

    // Check if the selected range is unavailable
    const unavailable = isDateRangeUnavailable(startDate, endDate);
    console.log("Custom date range availability check:", {
      unavailable,
      startDate,
      endDate,
    });

    if (unavailable) {
      console.log("Custom date range overlaps with unavailable dates");
      alert(
        "Une partie de votre intervalle de dates chevauche des dates indisponibles. Veuillez ajuster votre sélection."
      );
    }
  };

  // Suggest alternative available dates
  const suggestAvailableDates = () => {
    if (!unavailableDates.length) return [];

    // Get all unavailable date ranges
    const unavailableRanges = unavailableDates.map((range) => ({
      start: new Date(range.startDate),
      end: new Date(range.endDate),
    }));

    // Sort ranges by start date
    unavailableRanges.sort((a, b) => a.start - b.start);

    // Find gaps between unavailable ranges
    const availableRanges = [];
    let currentDate = new Date();

    for (const range of unavailableRanges) {
      if (currentDate < range.start) {
        // There's a gap before this unavailable range
        availableRanges.push({
          start: new Date(currentDate),
          end: new Date(range.start.getTime() - 24 * 60 * 60 * 1000), // day before unavailable starts
        });
      }
      // Move current date to after this unavailable range
      currentDate = new Date(range.end.getTime() + 24 * 60 * 60 * 1000);
    }

    // Add any remaining time after the last unavailable range
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3); // Look ahead 3 months

    if (currentDate < maxDate) {
      availableRanges.push({
        start: new Date(currentDate),
        end: new Date(maxDate),
      });
    }

    return availableRanges;
  };

  // Component to show available date suggestions
  const AvailableDateSuggestions = () => {
    const availableRanges = suggestAvailableDates();

    if (!availableRanges.length || !isDateUnavailable) return null;

    return (
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">
          📅 Dates disponibles suggérées:
        </h4>
        <div className="space-y-2">
          {availableRanges.slice(0, 3).map((range, index) => (
            <div key={index} className="text-sm text-blue-700">
              {range.start.toLocaleDateString()} -{" "}
              {range.end.toLocaleDateString()}
            </div>
          ))}
        </div>
        {availableRanges.length > 3 && (
          <p className="text-xs text-blue-600 mt-2">
            Et {availableRanges.length - 3} autres plages disponibles...
          </p>
        )}
      </div>
    );
  };

  // Create or get address helper
  const createOrGetAddress = async (addressData, addressId = null) => {
    try {
      if (addressId) {
        console.log(`Using existing address ID: ${addressId}`);
        const response = await fetch(`${API_BASE_URL}/address/${addressId}`);

        if (response.ok) {
          const result = await response.json();
          return result.data.address.id;
        }
      }

      // Create new address
      console.log("Creating new address:", addressData);
      const response = await fetch(`${API_BASE_URL}/address`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addressData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create address");
      }

      const result = await response.json();
      console.log("Address created successfully:", result);
      return result.data.address.id;
    } catch (error) {
      console.error("Error creating/getting address:", error);
      throw error;
    }
  };

  // Get extra service IDs by names
  const getExtraServiceIdsByNames = (serviceNames) => {
    if (!serviceNames || serviceNames.length === 0) return [];
    if (!extraServices || extraServices.length === 0) {
      console.warn("No extra services available");
      return [];
    }

    const serviceIds = [];
    for (const serviceName of serviceNames) {
      // Add null check for serviceName
      if (!serviceName || typeof serviceName !== "string") {
        console.warn(`Invalid service name: ${serviceName}`);
        continue;
      }

      const service = extraServices.find(
        (s) =>
          s &&
          s.name &&
          s.name.toLowerCase().trim() === serviceName.toLowerCase().trim()
      );
      if (service && service.id) {
        serviceIds.push(service.id);
      } else {
        console.warn(`Service not found: ${serviceName}`);
      }
    }
    console.log("Mapped service IDs:", serviceIds);
    return serviceIds;
  };

  // Map time slot to correct format
  const mapTimeSlotToEnum = (timeSlotString) => {
    const timeSlotMap = {
      "08:00 – 10:00": "08:00-10:00",
      "10:00 – 12:00": "10:00-12:00",
      "12:00 – 14:00": "12:00-14:00",
      "14:00 – 16:00": "14:00-16:00",
      "16:00 – 18:00": "16:00-18:00",
    };
    return timeSlotMap[timeSlotString] || "08:00-10:00";
  };

  // Generate guest token - améliorée
  const generateGuestToken = () => {
    // Générer un UUID-like token pour plus de sécurité
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    const guestToken = `guest_${timestamp}_${random}`;

    console.log("🎫 Guest token generated:", guestToken);
    return guestToken;
  };
  // Gestion améliorée du guestToken
  const manageGuestToken = () => {
    // Vérifier si un guestToken existe déjà dans le localStorage
    const existingToken = localStorage.getItem("guestToken");

    if (existingToken) {
      console.log("🎫 Existing guest token found:", existingToken);
      return existingToken;
    }

    // Sinon, générer un nouveau token
    const newToken = generateGuestToken();
    localStorage.setItem("guestToken", newToken);
    console.log("🎫 New guest token created and stored:", newToken);
    return newToken;
  };
  // UPDATED: Form validation for step 1 with date validation
  const validateStep1 = () => {
    const requiredFields = [
      "adresse_depart",
      "ville_depart",
      "cp_depart",
      "adresse_arrivee",
      "ville_arrivee",
      "cp_arrivee",
    ];

    // Always require personal info fields (backend might need them)
    requiredFields.push("prenom", "nom", "email");

    // Only require tel for non-authenticated users or if user doesn't have phone in profile
    if (!isAuthenticated || !formData.tel) {
      requiredFields.push("tel");
    }
    // MODIFICATION: Requérir le téléphone seulement s'il est vide
    // Même pour les utilisateurs authentifiés, car il peut manquer dans leur profil
    if (!formData.tel || formData.tel.trim() === "") {
      requiredFields.push("tel");
    }
    // NEW: If date is unavailable, require custom date range fields
    if (isDateUnavailable) {
      requiredFields.push("date_debut_custom", "date_fin_custom");
    } else {
      // Otherwise require the standard date field
      requiredFields.push("date_demenagement");
    }

    let isValid = true;

    requiredFields.forEach((fieldName) => {
      const field = fieldRefs.current[fieldName];
      if (!formData[fieldName]) {
        isValid = false;
        if (field) {
          field.style.borderColor = "#ef4444";
        }
      } else if (field) {
        field.style.borderColor = "#d1d5db";
      }
    });

    if (!isValid) {
      alert("Veuillez remplir tous les champs obligatoires.");
    }

    return isValid;
  };

  // 1. First, remove the export and fix the uploadImagesToServer function inside your component:

  // 2. Fix the handleSubmit function to properly call the upload function:
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("=== FORM SUBMISSION STARTED ===");

    if (!validateStep1()) return;

    // validate date availability ...
    if (formData.date_demenagement && !isDateUnavailable) {
      const availabilityResult = await checkDateAvailability(
        formData.date_demenagement,
        "demenagement"
      );
      if (!availabilityResult.isAvailable) {
        alert(
          "La date sélectionnée n'est plus disponible. Veuillez choisir une autre date ou spécifier une plage de dates préférée."
        );
        setIsDateUnavailable(true);
        return;
      }
    }

    if (isDateUnavailable) {
      if (!formData.date_debut_custom || !formData.date_fin_custom) {
        alert("Veuillez spécifier un intervalle de dates préféré complet.");
        return;
      }
      if (
        isDateRangeUnavailable(
          formData.date_debut_custom,
          formData.date_fin_custom
        )
      ) {
        alert(
          "L'intervalle de dates sélectionné chevauche des dates indisponibles. Veuillez ajuster."
        );
        return;
      }
    }

    setLoading(true);

    try {
      // ============================================================================
      // ÉTAPE 1: UPLOAD DES IMAGES (OPTIONNEL)
      // ============================================================================
      let uploadedImageUrls = [];
      if (uploadedImages.length > 0) {
        try {
          console.log("Uploading images to server...");
          uploadedImageUrls = await uploadImagesToServer(uploadedImages);
          console.log("Images uploaded successfully:", uploadedImageUrls);
        } catch (error) {
          console.error("Error uploading images:", error);
          setLoading(false);

          // Create a custom confirmation modal
          const userChoice = await new Promise((resolve) => {
            const modal = document.createElement("div");
            modal.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(0,0,0,0.5); display: flex; justify-content: center;
            align-items: center; z-index: 1000;
          `;

            modal.innerHTML = `
            <div style="background: white; padding: 20px; border-radius: 10px; text-align: center; max-width: 400px;">
              <h3>Erreur d'upload d'images</h3>
              <p>Erreur lors de l'upload des images. Souhaitez-vous continuer sans les images ?</p>
              <div style="margin-top: 20px;">
                <button id="cancel-btn" style="margin-right: 10px; padding: 10px 20px; background: #ef4444; color: white; border: none; border-radius: 5px;">Annuler</button>
                <button id="continue-btn" style="padding: 10px 20px; background: #3b82f6; color: white; border: none; border-radius: 5px;">Continuer sans images</button>
              </div>
            </div>
          `;

            document.body.appendChild(modal);

            document.getElementById("continue-btn").onclick = () => {
              document.body.removeChild(modal);
              resolve(true);
            };
            document.getElementById("cancel-btn").onclick = () => {
              document.body.removeChild(modal);
              resolve(false);
            };
          });

          if (!userChoice) {
            return;
          }

          setLoading(true);
        }
      }

      // ============================================================================
      // ÉTAPE 2: CRÉATION DES ADRESSES
      // ============================================================================
      const departureAddressData = {
        fullAddress: formData.adresse_depart,
        city: formData.ville_depart,
        postalCode: formData.cp_depart,
        housingType: formData.type_hebergement_depart,
        floor: parseInt(formData.etage_depart) || 0,
        ascenseur: formData.ascenseur_depart === "oui",
        accesDifficile: false,
      };

      const arrivalAddressData = {
        fullAddress: formData.adresse_arrivee,
        city: formData.ville_arrivee,
        postalCode: formData.cp_arrivee,
        housingType: formData.type_hebergement_arrivee,
        floor: parseInt(formData.etage_arrivee) || 0,
        ascenseur: formData.ascenseur_arrivee === "oui",
        accesDifficile: false,
      };

      const departureAddressId = await createOrGetAddress(
        departureAddressData,
        formData.departureAddressId
      );
      const arrivalAddressId = await createOrGetAddress(
        arrivalAddressData,
        formData.arrivalAddressId
      );

      // Rest of your existing code for selected items, services, dates, etc...
      const selectedItems = Object.entries(itemsState)
        .filter(([_, v]) => v.checked)
        .map(([id, v]) => ({ itemId: parseInt(id), quantity: v.quantity }));

      const servicesArray = Array.isArray(formData.services)
        ? formData.services
        : [];
      const extraServiceIds = getExtraServiceIdsByNames(servicesArray);

      let serviceDate,
        timeSlot,
        preferredStartDate = null,
        preferredEndDate = null;

      if (
        isDateUnavailable &&
        formData.date_debut_custom &&
        formData.date_fin_custom
      ) {
        serviceDate = formData.date_debut_custom;
        timeSlot = "08:00-10:00";
        preferredStartDate = formData.date_debut_custom;
        preferredEndDate = formData.date_fin_custom;
      } else {
        serviceDate = formData.date_demenagement;
        timeSlot = mapTimeSlotToEnum(formData.plage_horaire);
        preferredStartDate = formData.date_demenagement;
        preferredEndDate = formData.date_demenagement;
      }

      let notesParts = [];
      if (formData.notes) {
        notesParts.push(formData.notes);
      }
      if (
        isDateUnavailable &&
        formData.date_debut_custom &&
        formData.date_fin_custom
      ) {
        notesParts.push(
          `Demande spéciale: Plage de dates préférée du ${formData.date_debut_custom} au ${formData.date_fin_custom} (date initialement sélectionnée non disponible)`
        );
      }
      if (selectedItems.length > 0) {
        const itemsDescription = selectedItems
          .map((item) => {
            const itemData = itemsState[item.itemId];
            return `${itemData.name} (x${item.quantity})`;
          })
          .join(", ");
        notesParts.push(`Articles sélectionnés: ${itemsDescription}`);
      }
      if (formData.objets_speciaux) {
        notesParts.push(`Objets spéciaux: ${formData.objets_speciaux}`);
      }
      let notes = notesParts.join("\n");

      // ============================================================================
      // GESTION AMÉLIORÉE DU GUEST TOKEN
      // ============================================================================
      let finalGuestToken = guestToken;

      if (!isAuthenticated) {
        // S'assurer qu'un guestToken existe
        if (!finalGuestToken) {
          finalGuestToken = manageGuestToken();
          setGuestToken(finalGuestToken);
        }
        console.log("🎫 Using guest token for submission:", finalGuestToken);
      }

      // ============================================================================
      // PAYLOAD FINAL AVEC GESTION GUEST AMÉLIORÉE
      // ============================================================================
      const demandPayload = {
        serviceType: "demenagement",
        serviceDate,
        timeSlot,
        notes,
        consentRgpd: formData.rgpd,
        departureAddressId,
        arrivalAddressId,
        items: selectedItems,
        extraServiceIds,
        preferredStartDate,
        preferredEndDate,
        images: uploadedImageUrls.map((url) => ({ url: url })),

        // ✅ Conversion snake_case (formData) → camelCase attendu par l'API
        nombreCamions: formData.nombre_camions
          ? parseInt(formData.nombre_camions, 10)
          : undefined,
        nombreDemenageurs: formData.nombre_demenageurs
          ? parseInt(formData.nombre_demenageurs, 10)
          : undefined,
        descriptionManuelle: formData.description_manuelle || undefined,
      };

      if (isAuthenticated && userId) {
        // Utilisateur connecté
        demandPayload.userId = userId;
        console.log("👤 Demand with authenticated user:", userId);

        // Optionnel: garder les infos client même pour les utilisateurs connectés
        // si elles sont différentes du profil
        if (formData.prenom || formData.nom || formData.tel) {
          demandPayload.clientFirstName = formData.prenom;
          demandPayload.clientLastName = formData.nom;
          demandPayload.clientPhone = formData.tel;
          demandPayload.clientEmail = formData.email;
        }
      } else {
        // Mode guest - inclure toutes les infos client
        demandPayload.clientFirstName = formData.prenom;
        demandPayload.clientLastName = formData.nom;
        demandPayload.clientPhone = formData.tel || "Non fourni";
        demandPayload.clientEmail = formData.email;
        demandPayload.guestToken = finalGuestToken;

        console.log("👤 Guest demand with token:", finalGuestToken);
        console.log("📧 Guest email:", formData.email);
        console.log("📞 Guest phone:", formData.tel);
      }

      // Validation finale
      if (!demandPayload.serviceDate) {
        throw new Error("Date de service manquante");
      }
      if (!demandPayload.timeSlot) {
        throw new Error("Créneau horaire manquant");
      }
      if (
        !demandPayload.preferredStartDate ||
        !demandPayload.preferredEndDate
      ) {
        throw new Error("Dates préférées manquantes");
      }

      console.log("=== FINAL DEMAND PAYLOAD ===", demandPayload);
      console.log("📸 Uploaded Image URLs:", uploadedImageUrls);
      console.log(
        "📸 Formatted images for payload:",
        uploadedImageUrls.map((url) => ({ url: url }))
      );

      const response = await fetch(`${API_BASE_URL}/demandes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(demandPayload),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error("API Error Response:", error);
        console.error("Response Status:", response.status);
        throw new Error(
          error.message || error.error || "Échec de la création de la demande"
        );
      }

      const result = await response.json();
      console.log("✅ Demand created successfully:", result);

      // Stocker le guestToken retourné par l'API pour cohérence
      if (result.data?.guestToken) {
        localStorage.setItem("guestToken", result.data.guestToken);
        setGuestToken(result.data.guestToken);
      }

      setShowConfirmation(true);
    } catch (err) {
      console.error("Detailed error:", err);
      console.error("Error stack:", err.stack);
      alert(`Erreur lors de la soumission: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  // Get items for a specific category
  const getItemsForCategory = (categoryId) => {
    return categoryItems.filter((item) => item.category.id === categoryId);
  };

  // Toggle item selection
  const toggleItem = (itemId) => {
    setItemsState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        checked: !prev[itemId].checked,
      },
    }));
  };

  // Increase item quantity
  const increaseQuantity = (itemId) => {
    setItemsState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: prev[itemId].quantity + 1,
      },
    }));
  };

  // Decrease item quantity
  const decreaseQuantity = (itemId) => {
    setItemsState((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        quantity: Math.max(1, prev[itemId].quantity - 1),
      },
    }));
  };

  // Toggle summary display
  const handleShowSummary = () => {
    setShowSummary(!showSummary);
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Select/deselect all items
  const selectAll = () => {
    const newItemsState = { ...itemsState };
    Object.keys(newItemsState).forEach((key) => {
      newItemsState[key] = { ...newItemsState[key], checked: true };
    });
    setItemsState(newItemsState);
  };

  const deselectAll = () => {
    const newItemsState = { ...itemsState };
    Object.keys(newItemsState).forEach((key) => {
      newItemsState[key] = {
        ...newItemsState[key],
        checked: false,
        quantity: 1,
      };
    });
    setItemsState(newItemsState);
  };

  // Handle service checkbox change
  const handleServiceChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      // Ensure services is always an array
      let services = Array.isArray(prev.services) ? [...prev.services] : [];
      if (checked) {
        // Only add if not already present
        if (!services.includes(value)) {
          services.push(value);
        }
      } else {
        services = services.filter((s) => s !== value);
      }
      return { ...prev, services };
    });
  };

  // UPDATED: Handle input changes with enhanced date validation
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    console.log(`Input changed: ${name} = ${value}`);

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Check date availability when date is selected
    if (name === "date_demenagement" && value) {
      console.log("Date input changed, checking availability...");
      handleDateSelection(value);
    }

    // Handle custom date range changes
    if (name === "date_debut_custom" && value) {
      console.log("Custom start date changed:", value);
      handleCustomDateRange(value, formData.date_fin_custom);
    }

    if (name === "date_fin_custom" && value) {
      console.log("Custom end date changed:", value);
      handleCustomDateRange(formData.date_debut_custom, value);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <br />
      <br />
      <br />

      {/* Header Hero Section */}
      <div className="hero-gradient text-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-4">
              Demande de Soumission - Déménagement
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Obtenez votre devis personnalisé en quelques minutes
            </p>
            <div className="flex justify-center items-center space-x-8 text-sm">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Devis gratuit
              </div>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                Réponse sous 24h
              </div>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                100% sécurisé
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-8 relative z-10">
        {/* Progress Steps */}
        <div className="bg-white rounded-2xl card-shadow p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-4">
            <div
              className={`flex items-center rounded-full px-4 sm:px-6 py-2 sm:py-3 font-semibold ${
                currentStep === 1
                  ? "step-active"
                  : currentStep > 1
                  ? "step-completed"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              <span
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 text-sm ${
                  currentStep === 1
                    ? "bg-white bg-opacity-20"
                    : currentStep > 1
                    ? "bg-white bg-opacity-20"
                    : "bg-gray-400 text-white"
                }`}
              >
                1
              </span>
              Informations de base
            </div>

            <div className="hidden md:flex flex-1 h-1 bg-gray-200 mx-4">
              <div
                className="h-full bg-blue-500 progress-bar transition-all"
                style={{ width: currentStep >= 2 ? "100%" : "33%" }}
              ></div>
            </div>

            <div
              className={`flex items-center rounded-full px-4 sm:px-6 py-2 sm:py-3 font-semibold ${
                currentStep === 2
                  ? "step-active"
                  : currentStep > 2
                  ? "step-completed"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              <span
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 text-sm ${
                  currentStep === 2
                    ? "bg-white bg-opacity-20"
                    : currentStep > 2
                    ? "bg-white bg-opacity-20"
                    : "bg-gray-400 text-white"
                }`}
              >
                2
              </span>
              Détails
            </div>

            <div className="hidden md:flex flex-1 h-1 bg-gray-200 mx-4">
              <div
                className="h-full bg-blue-500 progress-bar transition-all"
                style={{ width: currentStep >= 3 ? "100%" : "0%" }}
              ></div>
            </div>

            <div
              className={`flex items-center rounded-full px-4 sm:px-6 py-2 sm:py-3 font-semibold ${
                currentStep === 3 ? "step-active" : "bg-gray-200 text-gray-600"
              }`}
            >
              <span
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center mr-2 sm:mr-3 text-sm ${
                  currentStep === 3
                    ? "bg-white bg-opacity-20"
                    : "bg-gray-400 text-white"
                }`}
              >
                3
              </span>
              Services & Finalisation
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-2xl card-shadow overflow-hidden">
          <form id="devisForm" className="p-8" onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            <div className={currentStep === 1 ? "slide-in" : "hidden"}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Personal Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <svg
                        className="w-5 h-5 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        ></path>
                      </svg>
                    </div>
                    Informations personnelles
                  </h2>

                  {isAuthenticated ? (
                    <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 mb-2">
                        ✅ Vous êtes connecté. Vos informations personnelles
                        sont pré-remplies.
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <strong>Nom:</strong>{" "}
                          {formData.nom || "Non disponible"}
                        </div>
                        <div>
                          <strong>Prénom:</strong>{" "}
                          {formData.prenom || "Non disponible"}
                        </div>
                        <div>
                          <strong>Email:</strong>{" "}
                          {formData.email || "Non disponible"}
                        </div>
                        <div>
                          <strong>Téléphone:</strong>{" "}
                          {formData.tel || "Non disponible"}
                        </div>
                      </div>

                      {!formData.tel && (
                        <p className="text-xs text-orange-600 mt-2">
                          Votre profil ne contient pas de numéro de téléphone.
                          Veuillez le saisir ci-dessous.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        💡 Vous n'êtes pas connecté. Vos informations seront
                        utilisées uniquement pour cette demande.
                      </p>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none peer"
                          ref={(el) => (fieldRefs.current.prenom = el)}
                        />
                        <label
                          htmlFor="prenom"
                          className={`floating-label ${
                            formData.prenom ? "active" : ""
                          }`}
                        >
                          Prénom *
                        </label>
                      </div>
                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none peer"
                          ref={(el) => (fieldRefs.current.nom = el)}
                        />
                        <label
                          htmlFor="nom"
                          className={`floating-label ${
                            formData.nom ? "active" : ""
                          }`}
                        >
                          Nom *
                        </label>
                      </div>
                    </div>

                    <div className="input-group relative transition-all duration-300">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none peer"
                        ref={(el) => (fieldRefs.current.email = el)}
                      />
                      <label
                        htmlFor="email"
                        className={`floating-label ${
                          formData.email ? "active" : ""
                        }`}
                      >
                        Adresse courriel *
                      </label>
                    </div>

                    <div className="input-group relative transition-all duration-300">
                      <input
                        type="tel"
                        name="tel"
                        value={formData.tel}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none peer"
                        ref={(el) => (fieldRefs.current.tel = el)}
                      />
                      <label
                        htmlFor="tel"
                        className={`floating-label ${
                          formData.tel ? "active" : ""
                        }`}
                      >
                        Numéro de téléphone *
                      </label>
                    </div>
                  </div>
                </div>

                {/* Date & Time Section */}
                <div className="border-2 border-purple-200 rounded-xl p-6 bg-purple-50">
                  <h3 className="font-semibold text-purple-800 mb-4 flex items-center">
                    <span className="text-2xl mr-2">🗓️</span>
                    Date & Horaire
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="input-group relative transition-all duration-300">
                      <input
                        type="date"
                        name="date_demenagement"
                        value={formData.date_demenagement}
                        onChange={handleInputChange}
                        onFocus={() => fetchUnavailableDates("demenagement")}
                        required
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-4 py-4 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                      />
                    </div>

                    {!isDateUnavailable && formData.date_demenagement && (
                      <div className="input-group relative transition-all duration-300">
                        <select
                          name="plage_horaire"
                          value={formData.plage_horaire}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                        >
                          <option value="">
                            Sélectionner une plage horaire
                          </option>
                          <option value="08:00 – 10:00">08:00 – 10:00</option>
                          <option value="10:00 – 12:00">10:00 – 12:00</option>
                          <option value="12:00 – 14:00">12:00 – 14:00</option>
                          <option value="14:00 – 16:00">14:00 – 16:00</option>
                          <option value="16:00 – 18:00">16:00 – 18:00</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {isDateUnavailable && formData.date_demenagement && (
                    <div className="mt-4 space-y-4">
                      <div className="p-4 border border-orange-300 bg-orange-50 rounded-lg text-orange-700">
                        ⚠️ Cette date n'est pas disponible pour les créneaux
                        standards.
                        <br />
                        👉 Veuillez spécifier un intervalle de dates préféré :
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="input-group relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date de début souhaitée
                          </label>
                          <input
                            type="date"
                            name="date_debut_custom"
                            value={formData.date_debut_custom || ""}
                            onChange={handleInputChange}
                            required={isDateUnavailable}
                            min={new Date().toISOString().split("T")[0]}
                            className="w-full px-4 py-4 border-2 border-orange-200 rounded-lg focus:border-orange-500 focus:outline-none bg-white"
                          />
                        </div>

                        <div className="input-group relative">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date de fin souhaitée
                          </label>
                          <input
                            type="date"
                            name="date_fin_custom"
                            value={formData.date_fin_custom || ""}
                            onChange={handleInputChange}
                            required={isDateUnavailable}
                            min={
                              formData.date_debut_custom ||
                              new Date().toISOString().split("T")[0]
                            }
                            className="w-full px-4 py-4 border-2 border-orange-200 rounded-lg focus:border-orange-500 focus:outline-none bg-white"
                          />
                        </div>
                      </div>

                      <AvailableDateSuggestions />
                    </div>
                  )}
                </div>
              </div>

              {/* Addresses Section */}
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      ></path>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                    </svg>
                  </div>
                  Adresses du déménagement
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Departure Address */}
                  <div className="border-2 border-red-200 rounded-xl p-6 bg-red-50">
                    <h3 className="font-semibold text-red-800 mb-4 flex items-center">
                      <span className="text-2xl mr-2">📦</span>
                      Adresse de départ
                    </h3>
                    <div className="space-y-4">
                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="text"
                          name="adresse_depart"
                          value={formData.adresse_depart}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none peer bg-white"
                          ref={(el) => (fieldRefs.current.adresse_depart = el)}
                        />
                        <label
                          htmlFor="adresse_depart"
                          className={`floating-label ${
                            formData.adresse_depart ? "active" : ""
                          }`}
                        >
                          Adresse complète *
                        </label>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="input-group relative transition-all duration-300">
                          <input
                            type="text"
                            name="ville_depart"
                            value={formData.ville_depart}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-4 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none peer bg-white"
                            ref={(el) => (fieldRefs.current.ville_depart = el)}
                          />
                          <label
                            htmlFor="ville_depart"
                            className={`floating-label ${
                              formData.ville_depart ? "active" : ""
                            }`}
                          >
                            Ville *
                          </label>
                        </div>
                        <div className="input-group relative transition-all duration-300">
                          <input
                            type="text"
                            name="cp_depart"
                            value={formData.cp_depart}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-4 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none peer bg-white"
                            ref={(el) => (fieldRefs.current.cp_depart = el)}
                          />
                          <label
                            htmlFor="cp_depart"
                            className={`floating-label ${
                              formData.cp_depart ? "active" : ""
                            }`}
                          >
                            Code postal *
                          </label>
                        </div>
                      </div>

                      <div className="input-group relative transition-all duration-300">
                        <select
                          name="type_hebergement_depart"
                          value={formData.type_hebergement_depart}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
                        >
                          <option value="">Type d'hébergement *</option>
                          <option value="Appartement">Appartement</option>
                          <option value="Maison">Maison</option>
                          <option value="Bureau">Bureau</option>
                        </select>
                      </div>

                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="number"
                          name="etage_depart"
                          min="0"
                          value={formData.etage_depart}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-red-200 rounded-lg focus:border-red-500 focus:outline-none bg-white"
                        />
                        <label
                          htmlFor="etage_depart"
                          className={`floating-label ${
                            formData.etage_depart ? "active" : ""
                          }`}
                        >
                          Étage *
                        </label>
                      </div>

                      <div>
                        <span className="block font-medium text-red-800 mb-2">
                          Ascenseur *
                        </span>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="ascenseur_depart"
                              value="oui"
                              checked={formData.ascenseur_depart === "oui"}
                              onChange={handleInputChange}
                              required
                            />
                            <span>Oui</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="ascenseur_depart"
                              value="non"
                              checked={formData.ascenseur_depart === "non"}
                              onChange={handleInputChange}
                            />
                            <span>Non</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrival Address */}
                  <div className="border-2 border-green-200 rounded-xl p-6 bg-green-50">
                    <h3 className="font-semibold text-green-800 mb-4 flex items-center">
                      <span className="text-2xl mr-2">🏠</span>
                      Adresse d'arrivée
                    </h3>
                    <div className="space-y-4">
                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="text"
                          name="adresse_arrivee"
                          value={formData.adresse_arrivee}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-green-200 rounded-lg focus:border-green-500 focus:outline-none peer bg-white"
                          ref={(el) => (fieldRefs.current.adresse_arrivee = el)}
                        />
                        <label
                          htmlFor="adresse_arrivee"
                          className={`floating-label ${
                            formData.adresse_arrivee ? "active" : ""
                          }`}
                        >
                          Adresse complète *
                        </label>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="input-group relative transition-all duration-300">
                          <input
                            type="text"
                            name="ville_arrivee"
                            value={formData.ville_arrivee}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-4 border-2 border-green-200 rounded-lg focus:border-green-500 focus:outline-none peer bg-white"
                            ref={(el) => (fieldRefs.current.ville_arrivee = el)}
                          />
                          <label
                            htmlFor="ville_arrivee"
                            className={`floating-label ${
                              formData.ville_arrivee ? "active" : ""
                            }`}
                          >
                            Ville *
                          </label>
                        </div>
                        <div className="input-group relative transition-all duration-300">
                          <input
                            type="text"
                            name="cp_arrivee"
                            value={formData.cp_arrivee}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-4 border-2 border-green-200 rounded-lg focus:border-green-500 focus:outline-none peer bg-white"
                            ref={(el) => (fieldRefs.current.cp_arrivee = el)}
                          />
                          <label
                            htmlFor="cp_arrivee"
                            className={`floating-label ${
                              formData.cp_arrivee ? "active" : ""
                            }`}
                          >
                            Code postal *
                          </label>
                        </div>
                      </div>

                      <div className="input-group relative transition-all duration-300">
                        <select
                          name="type_hebergement_arrivee"
                          value={formData.type_hebergement_arrivee}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-green-200 rounded-lg focus:border-green-500 focus:outline-none bg-white"
                        >
                          <option value="">Type d'hébergement *</option>
                          <option value="Appartement">Appartement</option>
                          <option value="Maison">Maison</option>
                          <option value="Bureau">Bureau</option>
                        </select>
                      </div>

                      <div className="input-group relative transition-all duration-300">
                        <input
                          type="number"
                          name="etage_arrivee"
                          min="0"
                          value={formData.etage_arrivee}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-4 border-2 border-green-200 rounded-lg focus:border-green-500 focus:outline-none bg-white"
                        />
                        <label
                          htmlFor="etage_arrivee"
                          className={`floating-label ${
                            formData.etage_arrivee ? "active" : ""
                          }`}
                        >
                          Étage *
                        </label>
                      </div>

                      <div>
                        <span className="block font-medium text-green-800 mb-2">
                          Ascenseur *
                        </span>
                        <div className="flex items-center space-x-6">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="ascenseur_arrivee"
                              value="oui"
                              checked={formData.ascenseur_arrivee === "oui"}
                              onChange={handleInputChange}
                              required
                            />
                            <span>Oui</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="ascenseur_arrivee"
                              value="non"
                              checked={formData.ascenseur_arrivee === "non"}
                              onChange={handleInputChange}
                            />
                            <span>Non</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
{/* Besoins logistiques */}
<div className="mt-8 border-2 border-yellow-200 rounded-xl p-6 bg-yellow-50">
  <h3 className="font-semibold text-yellow-800 mb-4 flex items-center">
    <span className="text-2xl mr-2">🚚</span>
    Besoins logistiques
  </h3>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="input-group relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Nombre de camions
      </label>
      <input
        type="number"
        name="nombre_camions"
        min="1"
        value={formData.nombre_camions}
        onChange={handleInputChange}
        placeholder="Ex: 1"
        className="w-full px-4 py-4 border-2 border-yellow-200 rounded-lg focus:border-yellow-500 focus:outline-none bg-white"
      />
    </div>
    <div className="input-group relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Nombre de déménageurs
      </label>
      <input
        type="number"
        name="nombre_demenageurs"
        min="1"
        value={formData.nombre_demenageurs}
        onChange={handleInputChange}
        placeholder="Ex: 2"
        className="w-full px-4 py-4 border-2 border-yellow-200 rounded-lg focus:border-yellow-500 focus:outline-none bg-white"
      />
    </div>
  </div>
</div>
              <button
                type="button"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" }); // scroll to top
                  nextStep(); // call your existing function
                }}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center shadow-md"
              >
                Continuer
                <svg
                  className="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
            </div>
{/* Step 2: Items Selection */}
<div className={currentStep === 2 ? "slide-in" : "hidden"}>
  <div className="space-y-10">
    <div className="container mx-auto px-4 py-4 max-w-[2000px]">
      <div className="bg-white rounded-3xl shadow-xl p-5 mb-1">
        <h1 className="text-3xl font-bold text-gray-800 mb-1 text-center">
          📦 Liste des Éléments à Déménager
        </h1>

        {/* Message d'instruction si rien n'est sélectionné */}
        {!modeSelection && (
          <p className="text-center text-gray-600 mt-2 mb-2">
            👇 Veuillez choisir comment vous souhaitez indiquer vos articles :
          </p>
        )}

        {/* Boutons de choix du mode */}
        <div className="flex justify-center gap-4 mt-4 mb-6">
          <button
            type="button"
            onClick={() => setModeSelection("articles")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md ${
              modeSelection === "articles"
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            📦 Articles
          </button>
          <button
            type="button"
            onClick={() => setModeSelection("manuelle")}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 shadow-md ${
              modeSelection === "manuelle"
                ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            ✍️ Description manuelle
          </button>
        </div>

        {showSummary && modeSelection === "articles" && (
          <div className="mb-6 p-4 bg-gray-100 rounded-lg">
            <h3 className="font-bold mb-2">
              Résumé des éléments sélectionnés :
            </h3>
            {Object.values(itemsState).some((item) => item.checked) ? (
              <ul className="space-y-1">
                {Object.entries(itemsState)
                  .filter(([_, value]) => value.checked)
                  .map(([itemId, value]) => (
                    <li key={itemId} className="flex justify-between">
                      <span>
                        {value.icon} {value.name}
                      </span>
                      <span>x{value.quantity}</span>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-gray-500">Aucun élément sélectionné</p>
            )}
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MODE ARTICLES : grille des catégories        */}
      {/* ============================================ */}
      {modeSelection === "articles" && (
        <div className="w-full mx-auto px-5 py-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 max-w-[1600px] mx-auto items-stretch">
            {categories
              .filter(
                (category) => getItemsForCategory(category.id).length > 0
              )
              .map((category) => (
                <div
                  key={category.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden min-w-0 flex flex-col h-full"
                >
                  {/* Header sticky (visible même quand on scrolle le contenu) */}
                  <div className="bg-gradient-to-r from-sky-200 to-blue-500 p-4 sm:p-4 flex items-center space-x-4 shadow-sm sticky top-0 z-10">
                    <span className="text-2xl sm:text-3xl flex-shrink-0">
                      {category.icon || "📦"}
                    </span>
                    <h5 className="text-lg sm:text-xl font-bold text-white truncate">
                      {category.name}
                    </h5>
                  </div>

                  <div className="divide-y divide-gray-120 flex-1 overflow-y-auto max-h-[80vh] sm:max-h-none">
                    {getItemsForCategory(category.id).map((item) => (
                      <div
                        key={item.id}
                        className={`p-3 sm:p-4 hover:bg-gray-50 transition-all duration-200 ${
                          itemsState[item.id]?.checked
                            ? "bg-blue-50"
                            : "bg-white"
                        }`}
                      >
                        <div className="flex items-start sm:items-center justify-between gap-2">
                          {/* Label et nom */}
                          <label className="flex items-center flex-1 min-w-0 space-x-2 sm:space-x-3 cursor-pointer">
                            <input
                              className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 rounded focus:ring-blue-400 flex-shrink-0"
                              type="checkbox"
                              checked={itemsState[item.id]?.checked || false}
                              onChange={() => toggleItem(item.id)}
                            />
                            <span className="flex flex-wrap items-center text-gray-700 text-sm sm:text-base font-medium min-w-0">
                              <span className="mr-1 sm:mr-2 text-xl sm:text-2xl flex-shrink-0">
                                {item.icon || "📦"}
                              </span>
                              <span className="break-words">
                                {item.name}
                              </span>
                            </span>
                          </label>

                          {/* Boutons de quantité */}
                          {itemsState[item.id]?.checked && (
                            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  decreaseQuantity(item.id);
                                }}
                                className="w-7 h-7 sm:w-8 sm:h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center text-lg sm:text-xl font-bold transition shadow-sm"
                              >
                                −
                              </button>
                              <span className="w-8 sm:w-10 text-center font-bold text-gray-800 text-sm sm:text-base bg-white rounded-lg py-1 border border-gray-300">
                                {itemsState[item.id].quantity}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  increaseQuantity(item.id);
                                }}
                                className="w-7 h-7 sm:w-8 sm:h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center text-lg sm:text-xl font-bold transition shadow-sm"
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODE DESCRIPTION MANUELLE : champ texte      */}
      {/* ============================================ */}
      {modeSelection === "manuelle" && (
        <div className="w-full max-w-3xl mx-auto px-5 py-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-200">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-2">✍️</span>
              Décrivez vos articles à déménager
            </h2>
            <textarea
              name="description_manuelle"
              value={formData.description_manuelle}
              onChange={handleInputChange}
              rows="8"
              placeholder="Ex: 1 canapé 3 places, 2 lits doubles, 1 réfrigérateur, 15 boîtes de vêtements..."
              className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            ></textarea>
            <p className="text-sm text-gray-500 mt-2">
              Listez librement vos meubles et objets avec leurs quantités.
            </p>
          </div>
        </div>
      )}

      {/* Photos : affichées seulement après avoir choisi un mode */}
      {modeSelection && (
        <div className="mb-8 bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center text-center justify-center">
            <span className="text-3xl mr-3">📸</span>
            Photos des articles à Déménager (Optionnel)
          </h2>

          <div className="text-center mb-6">
            <p className="text-gray-600 mb-2">
              <strong>Optionnel :</strong> Vous pouvez uploader des photos
              de vos articles spéciaux ou fragiles
            </p>
            <p className="text-sm text-gray-500">
              Formats acceptés: JPEG, PNG, GIF, WebP (max 5MB par image) •
              Maximum 5 images
            </p>
          </div>

          {/* Zone de drop et upload */}
          <div
            className="border-2 border-dashed border-blue-300 rounded-2xl p-8 text-center bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer mb-4"
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add(
                "border-blue-500",
                "bg-blue-100"
              );
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove(
                "border-blue-500",
                "bg-blue-100"
              );
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove(
                "border-blue-500",
                "bg-blue-100"
              );
              handleImageUpload({
                target: { files: e.dataTransfer.files },
              });
            }}
            onClick={() => document.getElementById("image-upload").click()}
          >
            <input
              id="image-upload"
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              className="hidden"
              ref={(el) => (fieldRefs.current.images = el)}
            />

            <div className="text-5xl mb-4">📁</div>
            <p className="text-lg font-semibold text-blue-700 mb-2">
              Cliquez ou glissez-déposez vos images ici
            </p>
            <p className="text-sm text-blue-600">
              Optionnel • Maximum 5 images
            </p>
          </div>

          {/* Message d'erreur */}
          {imageUploadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 font-medium">{imageUploadError}</p>
            </div>
          )}

          {/* Aperçu des images uploadées */}
          {uploadedImages.length > 0 && (
            <div className="mt-6">
              <h3 className="font-semibold text-gray-800 mb-3">
                📷 Photos uploadées ({uploadedImages.length}/5)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, index) => {
                  const image = uploadedImages[index];
                  return (
                    <div key={index} className="relative group">
                      {image ? (
                        <>
                          <img
                            src={image.preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg shadow-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-600 transition-colors"
                          >
                            ×
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                            {image.name}
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-24 flex items-center justify-center border border-dashed border-gray-300 rounded-lg text-gray-400">
                          {index + 1}️⃣ Vide
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {uploadedImages.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-700 text-sm">
                    ✅ {uploadedImages.length} photo(s) prête(s) pour
                    l'envoi.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>

    <div className="flex justify-between mt-8 px-4 sm:px-6 lg:px-8">
      <button
        type="button"
        onClick={() => {
          window.scrollTo({ top: 0, behavior: "smooth" }); // scroll vers le haut
          prevStep(); // appel de la fonction précédente
        }}
        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center shadow-md"
      >
        <svg
          className="w-5 h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Retour
      </button>
      <button
        type="button"
        onClick={() => {
          if (!modeSelection) {
            alert(
              "Veuillez choisir 'Articles' ou 'Description manuelle' avant de continuer."
            );
            return;
          }
          window.scrollTo({ top: 0, behavior: "smooth" }); // scroll to top
          nextStep(); // call your existing function
        }}
        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center shadow-md"
      >
        Continuer
        <svg
          className="w-5 h-5 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5l7 7-7 7"
          ></path>
        </svg>
      </button>
    </div>
  </div>
</div>

            {/* Step 3: Services and Finalization */}
            <div className={currentStep === 3 ? "animate-fadeIn" : "hidden"}>
              <div className="space-y-10">
                <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mr-4">
                    <svg
                      className="w-5 h-5 text-indigo-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg>
                  </div>
                  Services supplémentaires
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {extraServices && extraServices.length > 0 ? (
                    extraServices.map((service) => {
                      // Additional safety check for service object
                      if (!service || !service.id || !service.name) {
                        console.warn("Invalid service object:", service);
                        return null;
                      }

                      return (
                        <div
                          key={service.id}
                          className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors"
                        >
                          <label className="flex items-start cursor-pointer">
                            <input
                              type="checkbox"
                              name="services"
                              value={service.name.trim()}
                              checked={formData.services?.includes(
                                service.name.trim()
                              )}
                              onChange={handleServiceChange}
                              className="mt-1 mr-4"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-800 mb-2">
                                {service.icon} {service.name.trim()}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {service.description ||
                                  "Aucune description disponible"}
                              </p>
                            </div>
                          </label>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-2 text-center py-8 text-gray-500">
                      <p>
                        Aucun service supplémentaire disponible pour le moment.
                      </p>
                    </div>
                  )}
                </div>

                {/* Additional Information Section */}
                <div className="form-section space-y-6">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    📝 Informations supplémentaires
                  </h2>

                  <div>
                    <label
                      htmlFor="notes"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Commentaires et instructions particulières
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Contraintes d'accès, horaires spécifiques, précautions particulières..."
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-blue-400"
                    ></textarea>
                  </div>
                </div>

                {/* RGPD Consent */}
                <div className="mt-8 p-6 bg-gray-50 rounded-xl border-2 border-gray-200">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="rgpd"
                      checked={formData.rgpd}
                      onChange={handleInputChange}
                      required
                      className="mt-1 h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      <strong>Consentement RGPD *</strong>
                      <br />
                      J'accepte que mes données personnelles soient collectées
                      et traitées pour traiter ma demande de devis. Ces données
                      ne seront pas transmises à des tiers et seront supprimées
                      après traitement de ma demande.
                      <br />
                      <em>
                        Vous pouvez à tout moment demander la suppression de vos
                        données en nous contactant.
                      </em>
                    </span>
                  </label>
                </div>

                {/* Important Information */}
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                    ℹ️ Informations importantes
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>
                      ✅ Service de déménagement professionnel avec équipe
                      expérimentée
                    </li>
                    <li>📦 Emballage et protection de vos biens inclus</li>
                    <li>🚚 Transport sécurisé avec assurance</li>
                    <li>💼 Devis gratuit et sans engagement</li>
                    <li>⚡ Service disponible 7j/7</li>
                    <li>🔒 Respect total de vos biens et de votre domicile</li>
                  </ul>
                </div>

                {/* Summary before submission */}
                {(formData.date_demenagement ||
                  (formData.date_debut_custom && formData.date_fin_custom)) &&
                  Object.values(itemsState).some((item) => item.checked) && (
                    <div className="bg-green-50 border-l-4 border-green-500 rounded-xl p-6 shadow-sm">
                      <h3 className="text-lg font-bold text-gray-800 mb-3">
                        📋 Résumé de votre demande
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <strong>Date souhaitée :</strong>
                          <br />
                          {isDateUnavailable
                            ? `Du ${formData.date_debut_custom} au ${formData.date_fin_custom}`
                            : `${formData.date_demenagement} à ${formData.plage_horaire}`}
                        </div>
                        <div>
                          <strong>Éléments sélectionnés :</strong>
                          <br />
                          {
                            Object.values(itemsState).filter(
                              (item) => item.checked
                            ).length
                          }{" "}
                          catégories d'éléments
                        </div>
                        <div>
                          <strong>De :</strong>
                          <br />
                          {formData.adresse_depart}, {formData.ville_depart}
                        </div>
                        <div>
                          <strong>Vers :</strong>
                          <br />
                          {formData.adresse_arrivee}, {formData.ville_arrivee}
                        </div>
                        {formData.services.length > 0 && (
                          <div className="md:col-span-2">
                            <strong>Services supplémentaires :</strong>
                            <br />
                            {formData.services.join(", ")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>

              <div className="flex justify-between mt-12">
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" }); // scroll vers le haut
                    prevStep(); // appel de la fonction précédente
                  }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center shadow-md"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-12 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-6 h-6 mr-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                        ></path>
                      </svg>
                      Envoyer ma demande
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Demande envoyée !
              </h3>
              <p className="text-gray-600 mb-6">
                Votre demande de devis a été transmise avec succès. Notre équipe
                vous contactera sous 24h.
              </p>
              <button
                onClick={() => navigate("/")} // "/" = page d'accueil
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Demenagement;